import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Building2, Filter, Plus, Pencil, CheckCircle2, XCircle, Loader2, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ isActive, isDarkMode }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
    isActive
      ? 'bg-demoraes-green/10 text-demoraes-green'
      : isDarkMode ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-100 text-zinc-500'
  }`} data-testid={`sector-status-badge-${isActive ? 'active' : 'inactive'}`}>
    {isActive ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
    {isActive ? 'Ativo' : 'Inativo'}
  </span>
);

export const SectorsTab = ({ token, isDarkMode, availableTags = [] }) => {
  const [selectedTag, setSelectedTag] = useState('');
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newSectorName, setNewSectorName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selectedTag && availableTags.length > 0) {
      setSelectedTag(availableTags[0]);
    }
  }, [availableTags, selectedTag]);

  const fetchSectors = useCallback(async (tag) => {
    if (!token || !tag) { setSectors([]); return; }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/sectors`, { params: { tag } });
      if (response.data.success) setSectors(response.data.sectors || []);
    } catch (e) {
      console.error('Error fetching sectors:', e);
      setError('Não foi possível carregar os setores desta empresa.');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (selectedTag) fetchSectors(selectedTag);
  }, [selectedTag, fetchSectors]);

  const handleCreate = async () => {
    const name = newSectorName.trim();
    if (!name) return;
    if (!selectedTag) { setError('Selecione uma empresa antes de adicionar um setor.'); return; }
    setCreating(true);
    setError('');
    try {
      await axios.post(`${API}/admin/sectors`, { tag: selectedTag, name });
      setNewSectorName('');
      await fetchSectors(selectedTag);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao criar setor.');
    }
    setCreating(false);
  };

  const startEditing = (sector) => {
    setEditingId(sector.id);
    setEditingName(sector.name);
    setError('');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleRename = async (sectorId) => {
    const name = editingName.trim();
    if (!name) return;
    try {
      await axios.put(`${API}/admin/sectors/${sectorId}`, { name });
      cancelEditing();
      await fetchSectors(selectedTag);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao renomear setor.');
    }
  };

  const handleToggleActive = async (sector) => {
    try {
      const action = sector.is_active ? 'deactivate' : 'activate';
      await axios.patch(`${API}/admin/sectors/${sector.id}/${action}`);
      await fetchSectors(selectedTag);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao atualizar status do setor.');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header + company selector */}
      <div className={`rounded-lg border p-5 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Building2 size={20} className="text-demoraes-green" />
            <h3 className={`text-base font-bold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              Setores por Empresa
            </h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-100 text-zinc-600'}`}>
              {sectors.length} setores
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className={isDarkMode ? 'text-zinc-500' : 'text-zinc-400'} />
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className={`text-sm border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-demoraes-green/30 ${
                isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-200' : 'bg-white border-zinc-300 text-zinc-700'
              }`}
              data-testid="sectors-tag-filter"
            >
              <option value="">Selecione uma empresa...</option>
              {availableTags.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Add new sector */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-4">
          <input
            type="text"
            value={newSectorName}
            onChange={(e) => setNewSectorName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            placeholder="Nome do novo setor..."
            disabled={!selectedTag || creating}
            className={`flex-1 text-sm border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-demoraes-green/30 disabled:opacity-60 ${
              isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-200 placeholder-zinc-500' : 'bg-white border-zinc-300 text-zinc-700 placeholder-zinc-400'
            }`}
            data-testid="new-sector-input"
          />
          <button
            onClick={handleCreate}
            disabled={!selectedTag || !newSectorName.trim() || creating}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-demoraes-green hover:bg-demoraes-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="add-sector-button"
          >
            {creating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            Adicionar
          </button>
        </div>

        {error && (
          <p className={`mt-2 text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{error}</p>
        )}
      </div>

      {/* Table */}
      <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        {!selectedTag ? (
          <div className="text-center py-16">
            <Building2 size={40} className={`mx-auto mb-3 ${isDarkMode ? 'text-zinc-700' : 'text-zinc-300'}`} />
            <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Selecione uma empresa para ver seus setores.</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-demoraes-green" />
          </div>
        ) : sectors.length === 0 ? (
          <div className="text-center py-16">
            <Building2 size={40} className={`mx-auto mb-3 ${isDarkMode ? 'text-zinc-700' : 'text-zinc-300'}`} />
            <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
              Nenhum setor cadastrado para esta empresa ainda. Os respondentes verão a lista padrão até que setores sejam adicionados.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDarkMode ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-100 bg-zinc-50'}`}>
                  <th className={`text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Nome</th>
                  <th className={`text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Origem</th>
                  <th className={`text-center px-4 py-3 font-semibold text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Status</th>
                  <th className={`text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sectors.map((sector) => (
                  <tr key={sector.id}
                    className={`border-b transition-colors ${
                      isDarkMode ? 'border-zinc-800 hover:bg-zinc-800/50' : 'border-zinc-50 hover:bg-zinc-50'
                    }`}
                    data-testid={`sector-row-${sector.id}`}
                  >
                    <td className="px-4 py-3">
                      {editingId === sector.id ? (
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRename(sector.id);
                            if (e.key === 'Escape') cancelEditing();
                          }}
                          autoFocus
                          className={`text-sm border rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-demoraes-green/30 ${
                            isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-200' : 'bg-white border-zinc-300 text-zinc-700'
                          }`}
                          data-testid={`sector-rename-input-${sector.id}`}
                        />
                      ) : (
                        <span className={`font-medium ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{sector.name}</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
                      {sector.source === 'migration' ? 'Histórico (migrado)' : 'Manual'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge isActive={sector.is_active} isDarkMode={isDarkMode} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {editingId === sector.id ? (
                          <>
                            <button
                              onClick={() => handleRename(sector.id)}
                              className="text-xs font-medium text-demoraes-green hover:underline"
                              data-testid={`sector-save-${sector.id}`}
                            >
                              Salvar
                            </button>
                            <button
                              onClick={cancelEditing}
                              className={`text-xs font-medium ${isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-500 hover:text-zinc-700'}`}
                              data-testid={`sector-cancel-${sector.id}`}
                            >
                              Cancelar
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEditing(sector)}
                              title="Renomear"
                              className={`p-1.5 rounded-md transition-colors ${isDarkMode ? 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800' : 'text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100'}`}
                              data-testid={`sector-edit-${sector.id}`}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => handleToggleActive(sector)}
                              title={sector.is_active ? 'Desativar' : 'Reativar'}
                              className={`p-1.5 rounded-md transition-colors ${
                                sector.is_active
                                  ? (isDarkMode ? 'text-zinc-500 hover:text-red-400 hover:bg-red-950/30' : 'text-zinc-500 hover:text-red-600 hover:bg-red-50')
                                  : (isDarkMode ? 'text-zinc-500 hover:text-demoraes-green hover:bg-demoraes-green/10' : 'text-zinc-500 hover:text-demoraes-green hover:bg-demoraes-green/10')
                              }`}
                              data-testid={`sector-toggle-${sector.id}`}
                            >
                              {sector.is_active ? <XCircle size={14} /> : <RotateCcw size={14} />}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className={`text-xs px-1 ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>
        Desativar um setor apenas o oculta do formulário para novos respondentes; nenhum dado histórico de respostas é apagado ou alterado.
      </p>
    </div>
  );
};
